/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */


import React, { Fragment, useMemo } from 'react';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import Select, { GroupBase, StylesConfig } from 'react-select';
import { useHistory } from 'react-router-dom';
import { SettingStorage } from '../../services/SettingStorage';
import { InitiativeTags, RootInitiativeData } from '../../types/initiative';
import { AppRootMatcher } from '../../routes/appRootMatcher';
import { AppCode, AppConfig } from '../../types/app';
import { naturalSort } from '../../utils';
import './RootSwitcher.scss';
import SimpleTooltip from '../simple-tooltip';

interface BaseOption {
  value: string;
  label: string;
  firstInitiativeId: string;
}

export type OrgSwitcherInitiative = Pick<RootInitiativeData, '_id' | 'name' | 'firstInitiativeId' | 'tags' | 'appConfig' | 'permissionGroup' | 'type'>;

interface Props {
  viewMode?: 'select'
  selectStyles?: StylesConfig<BaseOption, false>,
  routeMatcher?: AppRootMatcher,
  rootInitiatives?: OrgSwitcherInitiative[],
}

interface AppBadgeProps {
  appConfig?: AppConfig;
  isStaffOrganization?: boolean;
}

const Badge = (props: { children: React.ReactNode }) => {
  const { children } = props;
  return (
    <span
      className={
        'd-none d-md-inline px-2 badge border border-ThemeTextMedium text-ThemeTextMedium user-select-none'
      }
    >
      {children}
    </span>
  );
};

const getAppType = (appCode?: AppCode | ''): string | undefined=> {
  switch (appCode) {
    case AppCode.CompanyTrackerStarter:
      return 'COMPANY TRACKER STARTER'
    case AppCode.CompanyTrackerPro:
      return 'COMPANY TRACKER PRO';
    case AppCode.CompanyTrackerEnterprise:
      return 'COMPANY TRACKER ENTERPRISE';
    case AppCode.SGXESGenome:
      return 'SGX ESGENOME';
    case AppCode.CompanyTracker:
      return 'COMPANY TRACKER';
    default:
      return;
  }
}

const getAppBadges = ({ appConfig, isStaffOrganization }: AppBadgeProps): JSX.Element[] => {
  const codes: string[] = [];

  if (appConfig?.code === AppCode.CompanyTrackerPro) {
    codes.push('Pro');
  } else if (appConfig?.code === AppCode.CompanyTrackerEnterprise) {
    codes.push('Enterprise');
  } else if (appConfig?.code === AppCode.CompanyTrackerStarter) {
    codes.push('Starter');
  }

  if (isStaffOrganization) {
    codes.push('Demo');
  }

  return codes.map((code) => (
    <span key={code} className='ml-2'>
      <SimpleTooltip
        text={
          <>
            You are on the {code} plan.<br />
            See Admin / Subscription info for details
          </>
        }
      >
        <Badge>{code}</Badge>
      </SimpleTooltip>
    </span>
  ));
};

export const OrganizationSwitcher = ({ viewMode, selectStyles, routeMatcher: routeHelper, rootInitiatives }: Props) => {
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const history = useHistory();

  const options = useMemo(() => {
    return routeHelper?.filterInitiatives((rootInitiatives || [])).map(r => ({
      value: r._id,
      label: r.name,
      firstInitiativeId: r.firstInitiativeId ?? r._id,
      isStaffOrganization: r.tags?.includes(InitiativeTags.StaffOrganization),
      appConfig: r.appConfig
    })).sort((a, b) => naturalSort(a.label, b.label)) ?? [];
  }, [rootInitiatives, routeHelper])

  if (!routeHelper || (options.length === 0)) {
    return null;
  }

  const combinedStyles: StylesConfig<BaseOption, false> = selectStyles ?? {
    container: (provided) => ({
      ...provided,
      display: 'inline-block',
      minWidth: '250px',
      margin: '0 .5rem'
    }),
    menu: (provider) => ({ ...provider, marginTop: '1px', }),
  }

  const selectedId = SettingStorage.getItem(routeHelper.storageKey);
  const selectedItem = options.find((o) => o.value === selectedId) ?? options[0];
  const onChange = (initiative: { value: string, firstInitiativeId: string }) => {
    SettingStorage.setItem(routeHelper.storageKey, initiative.value);
    history.push(routeHelper.generateUrl(initiative.firstInitiativeId));
  }

  const optionsByAppMap = new Map<AppCode | '', undefined | typeof options>(
    Object.values(AppCode).map(code => ([code, undefined]))
  );

  options.forEach(option => {
    const code = option.appConfig?.code ?? '';
    const optionsByApp = optionsByAppMap.get(code) ?? [];
    optionsByApp.push(option);
    optionsByAppMap.set(code, optionsByApp);
  });

  const isSelectMode = viewMode === 'select';
  if (isSelectMode) {
    const groupedOptions: GroupBase<BaseOption>[] = [];
    Array.from(optionsByAppMap.keys()).forEach(code => {
      groupedOptions.push({
        label: getAppType(code) ?? '',
        options: optionsByAppMap.get(code) ?? []
      });
    });
    const onMenuClose = () => setDropdownOpen(false);
    return (
      <div data-testid='root-switcher'>
        <Select
          styles={combinedStyles}
          onChange={(option) => {
            if (option) {
              onChange(option);
            }
          }}
          placeholder={'Search and select'}
          defaultMenuIsOpen={!isSelectMode}
          onMenuClose={onMenuClose}
          isSearchable={true}
          options={groupedOptions}
        />
      </div>
    );
  }

  return <>
    <div className='root-switcher-container dont_translate'>
      <Dropdown
        className='d-none d-md-inline ml-2'
        isOpen={dropdownOpen}
        toggle={() => setDropdownOpen(open => !open)}>
        <DropdownToggle caret color='transparent' className='ml-2 d-none d-md-inline text-truncate' style={{ maxWidth: '250px ' }}>
          {selectedItem?.label ?? 'Not Available'}
        </DropdownToggle>
        <DropdownMenu>
          {Array.from(optionsByAppMap.keys()).map(appCode => {
            const optionsByApp = optionsByAppMap.get(appCode);
            if (!optionsByApp) {
              return null;
            }
            const heading = getAppType(appCode);
            return <Fragment key={`appco_${appCode}`} >
              {heading ? <DropdownItem header>{heading}</DropdownItem> : null}
              {optionsByApp.map(option => (
                <DropdownItem key={`co_${option.value}`} onClick={() => onChange(option)}>{option.label}</DropdownItem>
              ))}
              <DropdownItem divider />
            </Fragment>
          })}
        </DropdownMenu>
      </Dropdown>
    </div>
    {getAppBadges(selectedItem)}
  </>
}
